import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  emptyInvoiceFilters,
  invoiceFiltersEqual,
  parseInvoiceFiltersFromSearchParams,
  serializeInvoiceFiltersToSearchParams,
  validateInvoiceFilterValues,
  type InvoiceFilterValues,
} from "@/domain/invoices/invoiceFilterState";
import {
  DEFAULT_DOWNLOAD_LIST_PAGE_SIZE,
  normalizeDownloadListPageSize,
} from "@/domain/invoices/downloadListPageSize";

const EMPTY_DEFAULT_STATUSES: string[] = [];

export function useInvoiceFilters(
  defaultStatuses: string[] = EMPTY_DEFAULT_STATUSES,
) {
  const [params, setParams] = useSearchParams();
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const applied = useMemo(
    () => parseInvoiceFiltersFromSearchParams(params, defaultStatuses),
    [params, defaultStatuses],
  );

  const [draft, setDraft] = useState<InvoiceFilterValues>(applied);
  const paramsKey = params.toString();

  useEffect(() => {
    const next = parseInvoiceFiltersFromSearchParams(
      new URLSearchParams(paramsKey),
      defaultStatuses,
    );
    setDraft((current) =>
      invoiceFiltersEqual(current, next)
        ? current
        : {
            customerIds: [...next.customerIds],
            statuses: [...next.statuses],
            dateFrom: next.dateFrom,
            dateTo: next.dateTo,
            amountMin: next.amountMin,
            amountMax: next.amountMax,
            dueDateFrom: next.dueDateFrom,
            dueDateTo: next.dueDateTo,
          },
    );
  }, [paramsKey, defaultStatuses]);

  const isDirty = useMemo(
    () => !invoiceFiltersEqual(draft, applied),
    [draft, applied],
  );

  const tableSearch = params.get("q") ?? "";
  const pageSize = normalizeDownloadListPageSize(
    Number(params.get("page_size") ?? String(DEFAULT_DOWNLOAD_LIST_PAGE_SIZE)) ||
      DEFAULT_DOWNLOAD_LIST_PAGE_SIZE,
  );
  const after = cursorStack[cursorStack.length - 1];

  const setTableSearch = useCallback(
    (q: string) => {
      const p = new URLSearchParams(params);
      if (q.trim()) p.set("q", q.trim());
      else p.delete("q");
      setParams(p, { replace: true });
    },
    [params, setParams],
  );

  const setPageSize = useCallback(
    (size: number) => {
      const p = new URLSearchParams(params);
      p.set("page_size", String(normalizeDownloadListPageSize(size)));
      setParams(p, { replace: true });
      setCursorStack([]);
    },
    [params, setParams],
  );

  const commitSearch = useCallback(
    (override?: InvoiceFilterValues) => {
      const values = override ?? draft;
      if (
        validateInvoiceFilterValues(values, {
          includeDueDate: Boolean(values.dueDateFrom || values.dueDateTo),
        }).length > 0
      ) {
        return;
      }
      const p = serializeInvoiceFiltersToSearchParams(values, params);
      setParams(p, { replace: true });
      setCursorStack([]);
    },
    [draft, params, setParams],
  );

  const resetDraft = useCallback(() => {
    setDraft(emptyInvoiceFilters(defaultStatuses));
  }, [defaultStatuses]);

  const goNext = useCallback((nextCursor: string) => {
    setCursorStack((s) => [...s, nextCursor]);
  }, []);

  const goPrev = useCallback(() => {
    setCursorStack((s) => s.slice(0, -1));
  }, []);

  const appliedSearchParams = useMemo(
    () => serializeInvoiceFiltersToSearchParams(applied, new URLSearchParams()),
    [applied],
  );

  return {
    draft,
    setDraft,
    applied,
    isDirty,
    tableSearch,
    pageSize,
    after,
    commitSearch,
    resetDraft,
    setTableSearch,
    setPageSize,
    goNext,
    goPrev,
    page: cursorStack.length + 1,
    canGoPrev: cursorStack.length > 0,
    appliedSearchParams,
  };
}
